import useSWR from 'swr';
import { useRef, useCallback, useSyncExternalStore } from 'react';

import axiosInstance, { endpoints } from 'src/lib/axios';

import { useAuthContext } from 'src/auth/hooks';

// ---------------------------------------------------------------------------
// localStorage settings for notification sounds
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'notification-sounds-settings';

export type NotificationSoundSettings = {
  enabled: boolean;
  volume: number; // 0–100
};

const DEFAULT_SETTINGS: NotificationSoundSettings = { enabled: true, volume: 70 };

// Simple pub/sub so multiple consumers stay in sync
const listeners = new Set<() => void>();
function emitChange() {
  listeners.forEach((l) => l());
}

function getRawSettings(): NotificationSoundSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(next: Partial<NotificationSoundSettings>) {
  const current = getRawSettings();
  const merged = { ...current, ...next };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  emitChange();
}

// Snapshot for useSyncExternalStore (must return same ref when unchanged)
let cachedSnapshot = getRawSettings();
function getSnapshot(): NotificationSoundSettings {
  const fresh = getRawSettings();
  if (
    fresh.enabled !== cachedSnapshot.enabled ||
    fresh.volume !== cachedSnapshot.volume
  ) {
    cachedSnapshot = fresh;
  }
  return cachedSnapshot;
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

/** React hook — returns current settings + setters */
export function useNotificationSoundSettings() {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    ...settings,
    setEnabled: (enabled: boolean) => saveSettings({ enabled }),
    setVolume: (volume: number) => saveSettings({ volume }),
  };
}

// ---------------------------------------------------------------------------
// Audio singletons (module-level so they persist across re-renders)
// ---------------------------------------------------------------------------
let kachingAudio: HTMLAudioElement | null = null;
let walletAudio: HTMLAudioElement | null = null;

function getKachingAudio(): HTMLAudioElement {
  if (!kachingAudio) {
    kachingAudio = new Audio('/sounds/videoplayback.wav');
  }
  return kachingAudio;
}

function getWalletAudio(): HTMLAudioElement {
  if (!walletAudio) {
    walletAudio = new Audio('/sounds/recarga.wav');
  }
  return walletAudio;
}

// ---------------------------------------------------------------------------
// Autoplay policy: track whether the user has interacted with the page
// ---------------------------------------------------------------------------
let userHasInteracted = false;

if (typeof window !== 'undefined') {
  const markInteracted = () => {
    userHasInteracted = true;
  };
  window.addEventListener('click', markInteracted, { once: true });
  window.addEventListener('keydown', markInteracted, { once: true });
}

function safePlay(audio: HTMLAudioElement): void {
  if (!userHasInteracted) return;
  const { volume } = getRawSettings();
  audio.volume = volume / 100;
  audio.currentTime = 0;
  audio.play().catch(() => {
    // Silencioso — autoplay bloqueado o audio no disponible
  });
}

// ---------------------------------------------------------------------------
// Hook — polling + playback (SWR-based)
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 15_000;

export function useNotificationSounds(): void {
  const { isAdmin } = useAuthContext();
  const isAdminUser = isAdmin();
  const { enabled } = useNotificationSoundSettings();

  const active = isAdminUser && enabled;

  const lastCheckRef = useRef<string>(new Date().toISOString());

  const fetcher = useCallback(async (url: string) => {
    const since = lastCheckRef.current;
    const now = new Date().toISOString();
    const { data } = await axiosInstance.get(url, { params: { since } });
    lastCheckRef.current = now;
    return data;
  }, []);

  useSWR(
    active ? endpoints.dashboard.notificationCheck : null,
    fetcher,
    {
      refreshInterval: POLL_INTERVAL_MS,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (data) => {
        const newPaidOrders: number = data?.newPaidOrders ?? 0;
        const newWalletRecharges: number = data?.newWalletRecharges ?? 0;

        if (newPaidOrders > 0) {
          safePlay(getKachingAudio());
        }

        if (newWalletRecharges > 0) {
          const delay = newPaidOrders > 0 ? 1500 : 0;
          setTimeout(() => safePlay(getWalletAudio()), delay);
        }
      },
    }
  );
}
